import { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  getArticleBySlug,
  getCategoryNeighbours,
  getRelatedArticles,
} from '@/utils/content';
import { isTrackArticle, markArticleRead } from '@/utils/learningProgress';
import SpreadCostCalculator from '@/components/feature/SpreadCostCalculator';

type ArticleReaderModalProps = {
  slug: string | null;
  onClose: () => void;
  onOpenArticle: (slug: string) => void;
};

export default function ArticleReaderModal({ slug, onClose, onOpenArticle }: ArticleReaderModalProps) {
  const article = slug ? getArticleBySlug(slug) : undefined;
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (slug) markArticleRead(slug);
  }, [slug]);

  useEffect(() => {
    if (!slug) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [slug, onClose]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [slug]);

  const related = useMemo(() => (article ? getRelatedArticles(article) : []), [article]);
  const neighbours = useMemo(
    () => (article ? getCategoryNeighbours(article.categorySlug, article.slug) : { prev: null, next: null }),
    [article],
  );
  const prevArticle = neighbours.prev;

  if (!article) return null;

  const scrollToSection = (id: string) => {
    const container = scrollRef.current;
    if (!container) return;
    const target = container.querySelector<HTMLElement>(`[data-section="${id}"]`);
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-2 sm:p-4 md:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={article.title}
    >
      <button
        type="button"
        aria-label="ปิดบทความ"
        onClick={onClose}
        className="absolute inset-0 cursor-pointer bg-foreground-950/50 backdrop-blur-sm"
      />

      <div className="relative z-10 flex max-h-[94dvh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-50 animate-fade-in">
        <header className="flex items-center justify-between gap-3 border-b border-background-200 bg-background-50 px-4 py-3 md:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-primary-100 px-2.5 py-1 text-xs font-medium text-primary-700">
              <i className="ri-folder-line text-sm" />
              {article.category}
            </span>
            <span className="hidden items-center gap-1.5 text-xs text-foreground-500 sm:flex">
              <i className="ri-time-line text-sm" />
              อ่านประมาณ {article.readingTime}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="ปิด"
            className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full border border-background-200 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </header>

        <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <div className="px-4 py-6 md:px-8 md:py-8">
            <h2 className="font-heading text-xl font-semibold leading-snug text-foreground-950 md:text-3xl">
              {article.title}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">{article.description}</p>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500 md:text-sm">
              <span className="flex items-center gap-1.5">
                <i className="ri-bar-chart-line text-base" />
                ระดับ {article.level}
              </span>
              {isTrackArticle(article.slug) && (
                <span className="flex items-center gap-1.5 font-medium text-primary-700">
                  <i className="ri-route-line text-base" />
                  บันทึกเข้าความคืบหน้าแล้ว
                </span>
              )}
            </div>

            <div className="mt-5 overflow-hidden rounded-xl border border-background-200">
              <img
                src={article.heroImage}
                alt={`${article.title} - เรียน Forex`}
                title={`${article.title} - Forex Academy`}
                className="h-48 w-full object-cover md:h-64"
              />
            </div>

            <div className="mt-6 rounded-xl border border-background-200 bg-background-100/50 p-4 md:p-5">
              <p className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-950">
                <i className="ri-list-unordered text-base text-primary-600" />
                สารบัญ
              </p>
              <ol className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {article.sections.map((section, index) => (
                  <li key={section.id}>
                    <button
                      type="button"
                      onClick={() => scrollToSection(section.id)}
                      className="flex w-full cursor-pointer items-start gap-2 text-left text-sm text-foreground-600 transition-colors hover:text-primary-700"
                    >
                      <span className="font-num text-xs text-primary-600">{String(index + 1).padStart(2, '0')}</span>
                      {section.heading}
                    </button>
                  </li>
                ))}
              </ol>
            </div>

            <div className="mt-6 flex flex-col gap-4">
              {article.intro.map((paragraph, index) => (
                <p key={index} className="text-sm leading-relaxed text-foreground-700 md:text-base">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-10 flex flex-col gap-10">
              {article.sections.map((section, index) => (
                <section key={section.id} data-section={section.id} className="scroll-mt-4">
                  <div className="flex items-baseline gap-3">
                    <span className="font-num text-sm font-semibold text-accent-600">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <h3 className="font-heading text-lg font-semibold leading-snug text-foreground-950 md:text-xl">
                      {section.heading}
                    </h3>
                  </div>

                  <div className="mt-3 flex flex-col gap-3.5">
                    {section.paragraphs.map((paragraph, pIndex) => (
                      <p key={pIndex} className="text-sm leading-relaxed text-foreground-700 md:text-[15px]">
                        {paragraph}
                      </p>
                    ))}

                    {'table' in section && section.table ? (
                      <div className="mt-1 overflow-hidden rounded-xl border border-background-200">
                        <div className="overflow-x-auto">
                          <table className="w-full min-w-[560px] border-collapse text-left">
                            <thead>
                              <tr className="bg-background-100">
                                {section.table.columns.map((column, colIndex) => (
                                  <th
                                    key={column}
                                    scope="col"
                                    className={`border-b border-background-200 px-4 py-3 text-xs font-semibold text-foreground-800 ${
                                      colIndex === 0 ? 'w-[24%]' : ''
                                    }`}
                                  >
                                    {column}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {section.table.rows.map((row, rowIndex) => (
                                <tr
                                  key={rowIndex}
                                  className={rowIndex % 2 === 1 ? 'bg-background-100/50' : 'bg-background-50'}
                                >
                                  {row.map((cell, cellIndex) => (
                                    <td
                                      key={cellIndex}
                                      className={`border-b border-background-200/70 px-4 py-3 align-top text-[13px] leading-relaxed ${
                                        cellIndex === 0
                                          ? 'font-semibold text-foreground-900'
                                          : 'text-foreground-700'
                                      }`}
                                    >
                                      {cell}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : null}

                    {'widget' in section && section.widget === 'spread-cost' ? <SpreadCostCalculator /> : null}

                    {section.bullets.length > 0 && (
                      <ul className="flex flex-col gap-2.5">
                        {section.bullets.map((bullet) => (
                          <li
                            key={bullet}
                            className="flex items-start gap-2.5 text-sm leading-relaxed text-foreground-700 md:text-[15px]"
                          >
                            <span className="mt-2 flex h-1.5 w-1.5 shrink-0 rounded-full bg-secondary-500" />
                            {bullet}
                          </li>
                        ))}
                      </ul>
                    )}

                    {section.steps.length > 0 && (
                      <div className="mt-1 flex flex-col gap-5">
                        {section.steps.map((step, sIndex) => (
                          <div
                            key={step.title}
                            className="overflow-hidden rounded-xl border border-background-200 bg-background-50"
                          >
                            <div className="flex items-center gap-3 border-b border-background-200 bg-background-100/60 px-4 py-3">
                              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-500 font-num text-xs font-semibold text-background-50">
                                {sIndex + 1}
                              </span>
                              <h4 className="font-heading text-sm font-semibold text-foreground-950">{step.title}</h4>
                            </div>
                            <div className="p-4">
                              <p className="text-sm leading-relaxed text-foreground-600">{step.description}</p>
                              <div className="mt-3 overflow-hidden rounded-lg border border-background-200">
                                <img
                                  src={step.image}
                                  alt={`${step.title} - ${article.title}`}
                                  title={`${step.title} - Forex Academy`}
                                  className="h-40 w-full object-cover md:h-52"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </section>
              ))}
            </div>

            {article.faqs.length > 0 && (
            <section className="mt-12 scroll-mt-4" data-section="faq">
              <h3 className="font-heading text-lg font-semibold text-foreground-950 md:text-xl">
                คำถามที่พบบ่อย
              </h3>
              <div className="mt-4 flex flex-col gap-3">
                {article.faqs.map((faq) => (
                  <details
                    key={faq.q}
                    className="group rounded-xl border border-background-200 bg-background-50 p-4 [&_summary::-webkit-details-marker]:hidden"
                  >
                    <summary className="flex cursor-pointer items-center justify-between gap-4 text-sm font-semibold text-foreground-900 md:text-base">
                      {faq.q}
                      <i className="ri-add-line text-lg text-primary-600 transition-transform group-open:rotate-45" />
                    </summary>
                    <div className="mt-3 flex flex-col gap-2.5">
                      <p className="text-sm leading-relaxed text-foreground-600">{faq.a}</p>
                      {faq.a2 ? (
                        <p className="text-sm leading-relaxed text-foreground-600">{faq.a2}</p>
                      ) : null}
                    </div>
                  </details>
                ))}
              </div>
            </section>
            )}

            <div className="mt-8 flex flex-col gap-4 rounded-xl border border-primary-200 bg-primary-50/70 p-4 sm:flex-row sm:items-center sm:justify-between md:p-5">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                  <i className="ri-translate-2 text-lg" />
                </span>
                <div>
                  <p className="font-heading text-sm font-semibold text-foreground-950 md:text-base">
                    ยังไม่คุ้นกับคำศัพท์ในบทความนี้?
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-foreground-600">
                    รวมคำศัพท์ Forex ที่มือใหม่ควรรู้ไว้ในหน้าคำศัพท์ พร้อมคำอธิบายง่าย ๆ
                  </p>
                </div>
              </div>
              <Link
                to="/glossary"
                onClick={onClose}
                className="inline-flex w-fit shrink-0 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                ดูคำศัพท์ Forex
                <i className="ri-arrow-right-line text-base" />
              </Link>
            </div>

            {prevArticle && (
              <div className="mt-8">
                <button
                  type="button"
                  onClick={() => onOpenArticle(prevArticle.slug)}
                  className="w-full cursor-pointer rounded-xl border border-background-200 bg-background-50 p-4 text-left transition-colors hover:border-primary-300"
                >
                  <span className="flex items-center gap-2 text-xs font-medium text-foreground-500">
                    <i className="ri-arrow-left-line text-base" />
                    บทความก่อนหน้า
                  </span>
                  <p className="mt-2 font-heading text-sm font-semibold leading-snug text-foreground-950">
                    {prevArticle.title}
                  </p>
                </button>
              </div>
            )}

            {related.length > 0 && (
              <section className="mt-8 border-t border-background-200 pt-6">
                <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                  <i className="ri-links-line text-base text-primary-600" />
                  บทความที่เกี่ยวข้อง
                </h3>
                <ul className="mt-3 flex flex-col divide-y divide-background-200 rounded-lg border border-background-200 bg-background-50">
                  {related.map((item) => (
                    <li key={item.slug}>
                      <button
                        type="button"
                        onClick={() => onOpenArticle(item.slug)}
                        className="group flex w-full cursor-pointer items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-primary-50/60"
                      >
                        <span className="h-14 w-20 shrink-0 overflow-hidden rounded-md bg-background-100">
                          <img
                            src={item.heroImage}
                            alt={`${item.title} - เรียน Forex`}
                            title={`${item.title} - Forex Academy`}
                            className="h-full w-full object-cover"
                          />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="text-[11px] font-medium text-secondary-900">{item.category}</span>
                          <span className="mt-0.5 line-clamp-2 block font-heading text-sm font-semibold leading-snug text-foreground-900 transition-colors group-hover:text-primary-700">
                            {item.title}
                          </span>
                        </span>
                        <i className="ri-arrow-right-s-line shrink-0 text-base text-foreground-300 transition-colors group-hover:text-primary-600" />
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}